import { ClassTypes } from '@/shared/constants/class-types';
import { EnvironmentSettingEnum } from '@/shared/constants';
import type { RenderCreateRequest } from '@/types/render';
import type { FirstRunState, FirstRunStep } from '@/types/profile';

/** Bumped when the flow changes shape, so analytics can split the versions. */
export const FIRST_RUN_WIZARD_VERSION = 1;

/** Longest custom idea the wizard accepts. */
export const FIRST_RUN_CUSTOM_MAX_LENGTH = 400;

/** After this long in "checking", the status line admits it is slow. */
export const FIRST_RUN_SLOW_AFTER_MS = 30_000;

export type FirstRunChoice =
  | { kind: 'beautify' }
  | { kind: 'custom'; text: string };

/**
 * Where the wizard opens. A user who already has a photo in the building goes
 * straight to the choice; anything the server did not name opens on upload.
 * `choose` without an angle is not a usable resume point, so it opens on
 * upload too.
 */
export function resolveInitialStep(state: FirstRunState): FirstRunStep {
  return state.resume_step === 'choose' && state.angle_id ? 'choose' : 'upload';
}

/** Custom text as it is sent, or null when there is nothing to send. */
export function normalizeCustomText(text: string): string | null {
  const trimmed = text.trim().slice(0, FIRST_RUN_CUSTOM_MAX_LENGTH);
  return trimmed === '' ? null : trimmed;
}

/**
 * The render request for the wizard's one choice. Deliberately minimal: every
 * setting the wizard does not ask about (effort, ambition, style, colors,
 * brands, seed, model, Hi-Res, thinking) is left to the server's defaults.
 */
export function buildFirstRunRenderRequest(
  choice: FirstRunChoice
): RenderCreateRequest {
  const base = { version: 1, mode: 'explore' as const };
  if (choice.kind === 'beautify') {
    return {
      data: {
        type: ClassTypes.RENDER,
        attributes: {
          ...base,
          environment_setting: EnvironmentSettingEnum.CLEAR_DEBRIS,
        },
      },
    };
  }
  const text = normalizeCustomText(choice.text);
  if (text === null) {
    throw new Error('A custom first-run choice needs text');
  }
  return {
    data: {
      type: ClassTypes.RENDER,
      attributes: { ...base, prompts: { prompt_concept: text } },
    },
  };
}
